/**
 * Ein Stück Listenzustand, das in der Adresse steht statt in `useState`.
 *
 * ─── Warum ─────────────────────────────────────────────────────────────
 * Ein Rückweg, der stimmt, ist nur die halbe Sache: Führt er auf eine Liste,
 * die sich beim Betreten zurückgesetzt hat, war er trotzdem umsonst. Bis zum
 * 10.09.2026 lagen Suchbegriff, Sortierung, Seite und Kalenderwoche in
 * `useState` — und `useState` lebt so lange wie die Komponente. Wer eine Zeile
 * öffnete, verlor die Liste; wer die Seite neu lud, ebenso; und teilen ließ
 * sich eine gefilterte Ansicht gar nicht.
 *
 * In der Adresse überlebt derselbe Zustand alles vier: Vorwärts, Zurück,
 * Neuladen, Weitergeben.
 *
 * ─── Warum nuqs und nicht `useSearchParams` ────────────────────────────
 * Die erste Fassung schrieb selbst, über `setSearchParams(prev => …)`. Das
 * ging so lange gut, wie *ein* Haken je Ereignis schrieb. Ein Suchfeld
 * schreibt aber zwei: den Begriff und die zurückgesetzte Seite. Und React
 * Router baut den Aktualisierer auf dem Stand des laufenden Renders auf —
 * `prev` ist nicht frischer als eine Kopie von oben. Beide Aufrufe sahen
 * dieselbe alte Adresse, beide navigierten mit `replace`, der zweite gewann:
 * Der getippte Begriff war weg, ehe er auf dem Bildschirm stand. Drei
 * Übersichten hatten ein totes Suchfeld.
 *
 * Das ist keine Nachlässigkeit an einer Stelle, sondern die Bauart: Mehrere
 * unabhängige Haken schreiben auf dieselbe Adresse, ohne voneinander zu
 * wissen. `nuqs` sammelt alle Änderungen eines Ereignisses ein und schreibt
 * sie in *einem* Zug — der Fehler kann so nicht mehr entstehen, auch nicht
 * durch den nächsten Haken, den jemand danebenstellt.
 *
 * ─── Zwei Entscheidungen, die man sonst falsch trifft ───────────────────
 * **Der Vorgabewert steht nicht in der Adresse** (`clearOnDefault`, bei nuqs
 * die Voreinstellung). Sonst trüge jede Liste beim ersten Blick
 * `?such=&seite=0&sort=` vor sich her — unlesbar, unteilbar, und beim
 * Vergleich zweier Adressen wäre nichts mehr gleich.
 *
 * **Geschrieben wird mit `replace`** (`history`, bei nuqs ebenfalls die
 * Voreinstellung). Ein Suchfeld schreibt bei jedem Tastendruck; mit `push`
 * läge danach jeder Zwischenstand im Verlauf, und der Zurück-Knopf des
 * Browsers buchstabierte den Suchbegriff rückwärts, statt zur vorigen Seite
 * zu führen. Der Verlauf soll Seiten enthalten, keine Tastendrücke.
 * Ausnahme per `history: 'push'`: ein Wert, der selbst ein Ort ist, an den
 * man zurückwill — das Schuljahr der Schul-Detailseite.
 *
 * Nebenbei bremst nuqs die Adressänderungen von sich aus (50 ms, in Safari
 * 120 ms). Das ist kein Luxus: Safari wirft bei etwa hundert
 * `replaceState`-Aufrufen in dreißig Sekunden einen `SecurityError`, und ein
 * ungebremstes Suchfeld läuft da hinein.
 *
 * ─── Warum `setzen` auch eine Funktion annimmt ──────────────────────────
 * Wer mehrere Einträge desselben Parameters aus einer einzigen Handlung
 * heraus verändert (z. B. zwei Spaltenfilter im selben Klick setzen), darf
 * den zweiten Aufruf nicht auf demselben Render-Stand aufbauen wie den
 * ersten — sonst genau die oben beschriebene Falle: zwei Schreibvorgänge im
 * selben Ereignis, der zweite überschreibt den ersten. Die Funktionsform
 * bekommt den zuletzt geschriebenen Wert (mit angewandter Vorgabe) gereicht
 * und nicht den aus dem Render geschlossenen — mehrere Aufrufe im selben
 * Ereignis setzen so korrekt aufeinander auf.
 */

import { useCallback } from 'react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';

/**
 * Die Einstellung, die `NuqsAdapter` in `App.tsx` braucht, damit diese Haken
 * halten, was sie versprechen.
 *
 * `shallow: false` ist Pflicht, nicht Geschmack: Ohne das schreibt nuqs die
 * Adresse an React Router vorbei direkt in die History, und `useLocation()`
 * bleibt stehen. Genau davon lebt aber der Rueckweg der Listen —
 * `LehrkraeftePage` reicht `location.search` an die Detailseite weiter, damit
 * der Brotkrumen-Pfad auf dieselbe gefilterte Liste zurueckfuehrt. Die App hat
 * keine Loader, das Mitfuehren kostet also nichts.
 *
 * Sie steht hier und nicht als Literal in `App.tsx`, damit der Test dieselbe
 * Einstellung benutzt wie die App und beide nicht auseinanderlaufen koennen.
 */
export const NUQS_ADAPTER_OPTIONS = { shallow: false } as const;

/**
 * Liest und schreibt einen Suchparameter wie ein `useState`.
 *
 * @param key Name des Parameters in der Adresse. Kurz und deutsch — er steht
 *   sichtbar in der Adresszeile (`?such=mathe`).
 * @param fallback Was gilt, wenn der Parameter fehlt. Wird nie geschrieben.
 * @param options.history `replace` (Vorgabe) oder `push` — siehe oben.
 */
export function useUrlState(
  key: string,
  fallback: string,
  options: { history?: 'replace' | 'push' } = {}
): [string, (value: string | ((prev: string) => string)) => void] {
  const [value, setValue] = useQueryState(key, parseAsString.withDefault(fallback));
  const { history } = options;

  const setzen = useCallback(
    (next: string | ((prev: string) => string)) => {
      void setValue((prev) => {
        // `prev` trägt hier bereits die Vorgabe — derselbe Wert, den der
        // Aufrufer als erstes Element des Tupels sieht.
        const resolved = typeof next === 'function' ? next(prev) : next;
        // `null` heißt bei nuqs „Parameter weg". Der leere Wert bedeutet auch
        // dann „zurück auf die Vorgabe", wenn die Vorgabe nicht leer ist —
        // sonst stünde `?woche=` in der Adresse und meinte nichts.
        return resolved === '' ? null : resolved;
      }, history ? { history } : undefined);
    },
    [setValue, history]
  );

  return [value, setzen];
}

/**
 * Dasselbe für eine Zahl.
 *
 * Was sich nicht als Zahl lesen lässt, gilt als nicht gesetzt: Eine Adresse
 * ist Benutzereingabe — `?seite=abc` darf eine Liste nicht in einen Zustand
 * bringen, aus dem sie nicht zurückfindet.
 */
export function useUrlNumber(key: string, fallback: number): [number, (value: number) => void] {
  const [value, setValue] = useQueryState(key, parseAsInteger.withDefault(fallback));

  const setzen = useCallback((next: number) => void setValue(next), [setValue]);

  return [value, setzen];
}
